import { HttpTypes } from "@medusajs/types"
import { analyticsAllowed, onConsentChange } from "@/lib/utils/consent"

type GtagFn = (...args: unknown[]) => void

declare global {
  interface Window {
    gtag?: GtagFn
    dataLayer?: unknown[]
  }
}

export const initializeGa4 = (measurementId: string | undefined) => {
  if (typeof window === "undefined" || !measurementId?.trim()) return

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag() {
      // gtag.js only executes commands pushed as an `arguments` object, the
      // exact shape of Google's own snippet. A rest-parameter array looks the
      // same but is silently ignored, so the js/config commands never ran and
      // GA4 received no page views at all.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments)
    }

  window.gtag("js", new Date())
  window.gtag("config", measurementId.trim(), { send_page_view: true })
}

let ga4Loaded = false

/**
 * Inject the GA4 gtag script and initialize it. Called ONLY after analytics
 * consent is granted — GA4 is never loaded on page render.
 */
const loadGa4 = (measurementId: string) => {
  if (typeof window === "undefined" || ga4Loaded) return
  ga4Loaded = true

  const script = document.createElement("script")
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`
  script.async = true
  document.head.appendChild(script)

  initializeGa4(measurementId)
}

/**
 * Consent-gated GA4 setup. Loads GA4 immediately if analytics is already
 * granted, otherwise waits and loads it the moment the visitor opts in.
 * Returns an unsubscribe function for the consent listener.
 */
export const setupGa4WithConsent = (
  measurementId: string | undefined,
): (() => void) => {
  if (typeof window === "undefined" || !measurementId?.trim()) return () => {}
  const id = measurementId.trim()

  if (analyticsAllowed()) {
    loadGa4(id)
    return () => {}
  }

  return onConsentChange((state) => {
    if (state.analytics === "granted") loadGa4(id)
  })
}

const safeGtag = (event: string, params: Record<string, unknown>) => {
  if (typeof window === "undefined") return
  if (typeof window.gtag === "function") {
    window.gtag("event", event, params)
    return
  }
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event, ...params })
  }
}

export const trackOrderClick = (
  event:
    | "order_online_click"
    | "preorder_add_to_cart"
    | "preorder_checkout"
    | "catering_quote_submit"
    | "catering_quote_start"
    | "catering_segment_click"
    | "catering_bundle_headcount"
    | "catering_service_quote_click"
    | "catering_outbound_click"
    | "directions_click"
    | "phone_click"
    | "email_click"
    | "menu_tab_click"
    | "collab_application_submit",
  location: string,
  extra?: Record<string, unknown>
) => {
  safeGtag(event, { location, ...extra })
}

// ─────────────────────────────────────────────────────────────────────
// GA4 Enhanced Ecommerce
//
// All events follow the GA4 ecommerce schema so the standard reports
// (Monetization → Ecommerce purchases, Funnel exploration) work without
// custom configuration.
//
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
// ─────────────────────────────────────────────────────────────────────

const FALLBACK_CURRENCY = "USD"

/**
 * GA4 expects amounts as decimal dollars. Medusa v2 already returns
 * cart/order totals and prices in major currency units (dollars), so the
 * value is used as-is — no cents conversion.
 */
const toDecimalAmount = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const normaliseCurrency = (value: string | null | undefined) =>
  (value?.trim() || FALLBACK_CURRENCY).toUpperCase()

type GA4Item = {
  item_id: string
  item_name: string
  item_brand?: string
  item_category?: string
  item_variant?: string
  price?: number
  quantity?: number
  index?: number
}

type LineItemLike = {
  product_id?: string | null
  product_handle?: string | null
  product_title?: string | null
  product_collection?: { title?: string | null } | null
  variant_id?: string | null
  variant_title?: string | null
  variant_sku?: string | null
  title?: string | null
  unit_price?: number | string | null
  quantity?: number | null
}

type ProductLike = {
  id: string
  handle?: string | null
  title?: string | null
  collection?: { title?: string | null } | null
  variants?: ReadonlyArray<{
    id: string
    title?: string | null
    calculated_price?: { calculated_amount?: number | string | null } | null
  }> | null
}

const lineItemToGa4Item = (
  item: LineItemLike,
  index: number
): GA4Item => ({
  item_id: item.product_handle ?? item.product_id ?? item.variant_sku ?? `item_${index}`,
  item_name: item.product_title ?? item.title ?? "(unknown)",
  item_brand: "+Bake Cafe",
  item_category: item.product_collection?.title ?? undefined,
  item_variant: item.variant_title ?? undefined,
  price: toDecimalAmount(item.unit_price),
  quantity: item.quantity ?? 1,
  index,
})

const productToGa4Item = (product: ProductLike): GA4Item => {
  const firstVariant = product.variants?.[0]
  return {
    item_id: product.handle ?? product.id,
    item_name: product.title ?? "(unknown)",
    item_brand: "+Bake Cafe",
    item_category: product.collection?.title ?? undefined,
    item_variant: firstVariant?.title ?? undefined,
    price: toDecimalAmount(firstVariant?.calculated_price?.calculated_amount),
    quantity: 1,
  }
}

/**
 * `view_item` — fired when a customer lands on a PDP.
 */
export const trackViewItem = (product: ProductLike, currencyCode?: string | null) => {
  const item = productToGa4Item(product)
  safeGtag("view_item", {
    currency: normaliseCurrency(currencyCode),
    value: item.price ?? 0,
    items: [item],
  })
}

/**
 * `add_to_cart` — fired AFTER a successful add-to-cart mutation, not on
 * intent click. The intent click is tracked via `preorder_add_to_cart`.
 */
export const trackAddToCart = ({
  product,
  variant,
  quantity,
  currencyCode,
}: {
  product: ProductLike | { id: string; title?: string | null; handle?: string | null; collection?: { title?: string | null } | null }
  variant?: {
    id: string
    title?: string | null
    calculated_price?: { calculated_amount?: number | string | null } | null
  } | null
  quantity: number
  currencyCode?: string | null
}) => {
  const unitPrice = toDecimalAmount(variant?.calculated_price?.calculated_amount)
  const item: GA4Item = {
    item_id: product.handle ?? product.id,
    item_name: product.title ?? "(unknown)",
    item_brand: "+Bake Cafe",
    item_category: product.collection?.title ?? undefined,
    item_variant: variant?.title ?? undefined,
    price: unitPrice,
    quantity,
  }
  safeGtag("add_to_cart", {
    currency: normaliseCurrency(currencyCode),
    value: unitPrice * quantity,
    items: [item],
  })
}

/**
 * `view_cart` — fired when the customer lands on the cart page.
 */
export const trackViewCart = (cart: HttpTypes.StoreCart | null | undefined) => {
  if (!cart) return
  const items = (cart.items ?? []).map(
    (item, index) => lineItemToGa4Item(item as LineItemLike, index)
  )
  safeGtag("view_cart", {
    currency: normaliseCurrency(cart.currency_code),
    value: toDecimalAmount(cart.total),
    items,
  })
}

/**
 * `begin_checkout` — fired when the customer lands on /checkout.
 */
export const trackBeginCheckout = (cart: HttpTypes.StoreCart | null | undefined) => {
  if (!cart) return
  const items = (cart.items ?? []).map(
    (item, index) => lineItemToGa4Item(item as LineItemLike, index)
  )
  safeGtag("begin_checkout", {
    currency: normaliseCurrency(cart.currency_code),
    value: toDecimalAmount(cart.total),
    items,
  })
}

/**
 * `add_shipping_info` — fired when the customer commits the address +
 * delivery (pickup) step.
 */
export const trackAddShippingInfo = (
  cart: HttpTypes.StoreCart | null | undefined,
  shippingTier?: string
) => {
  if (!cart) return
  const items = (cart.items ?? []).map(
    (item, index) => lineItemToGa4Item(item as LineItemLike, index)
  )
  safeGtag("add_shipping_info", {
    currency: normaliseCurrency(cart.currency_code),
    value: toDecimalAmount(cart.total),
    shipping_tier: shippingTier ?? "pickup",
    items,
  })
}

/**
 * `add_payment_info` — fired when the customer commits the payment step.
 */
export const trackAddPaymentInfo = (
  cart: HttpTypes.StoreCart | null | undefined,
  paymentType?: string
) => {
  if (!cart) return
  const items = (cart.items ?? []).map(
    (item, index) => lineItemToGa4Item(item as LineItemLike, index)
  )
  safeGtag("add_payment_info", {
    currency: normaliseCurrency(cart.currency_code),
    value: toDecimalAmount(cart.total),
    payment_type: paymentType ?? "card",
    items,
  })
}

/**
 * `purchase` — the conversion event. Must dedupe on `transaction_id`
 * (we use `order.display_id`) so a remount or back-button doesn't
 * inflate revenue.
 */
type OrderForPurchase = Pick<
  HttpTypes.StoreOrder,
  | "display_id"
  | "id"
  | "currency_code"
  | "total"
  | "tax_total"
  | "shipping_total"
  | "discount_total"
> & {
  items?: ReadonlyArray<LineItemLike> | null
  promotions?: ReadonlyArray<{ code?: string | null }> | null
}

export const trackPurchase = (order: OrderForPurchase) => {
  const transactionId = String(order.display_id ?? order.id ?? "")
  if (!transactionId) return
  if (hasFiredPurchase(transactionId)) return

  const items = (order.items ?? []).map(
    (item, index) => lineItemToGa4Item(item as LineItemLike, index)
  )
  const couponCodes = (order.promotions ?? [])
    .map((promo) => promo?.code)
    .filter((code): code is string => Boolean(code))

  safeGtag("purchase", {
    transaction_id: transactionId,
    currency: normaliseCurrency(order.currency_code),
    value: toDecimalAmount(order.total),
    tax: toDecimalAmount(order.tax_total),
    shipping: toDecimalAmount(order.shipping_total),
    items,
    ...(couponCodes.length ? { coupon: couponCodes.join(",") } : {}),
  })

  markPurchaseFired(transactionId)
}

const PURCHASE_DEDUPE_STORAGE_KEY = "plusbake.ga4.purchase_fired"
const PURCHASE_DEDUPE_TTL_MS = 24 * 60 * 60 * 1000

const hasFiredPurchase = (transactionId: string): boolean => {
  if (typeof window === "undefined") return false
  try {
    const raw = window.sessionStorage.getItem(PURCHASE_DEDUPE_STORAGE_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as Record<string, number>
    const firedAt = parsed[transactionId]
    if (typeof firedAt !== "number") return false
    if (Date.now() - firedAt > PURCHASE_DEDUPE_TTL_MS) {
      delete parsed[transactionId]
      window.sessionStorage.setItem(
        PURCHASE_DEDUPE_STORAGE_KEY,
        JSON.stringify(parsed)
      )
      return false
    }
    return true
  } catch {
    return false
  }
}

const markPurchaseFired = (transactionId: string) => {
  if (typeof window === "undefined") return
  try {
    const raw = window.sessionStorage.getItem(PURCHASE_DEDUPE_STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as Record<string, number>) : {}
    parsed[transactionId] = Date.now()
    window.sessionStorage.setItem(
      PURCHASE_DEDUPE_STORAGE_KEY,
      JSON.stringify(parsed)
    )
  } catch {
    /* ignore */
  }
}
